import { FC, useState, useEffect } from "react";
import {
	useController,
	useFormContext,
	useFormState,
	useWatch
} from "react-hook-form";
import { QuestionActions } from "..";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import HeadingCoverUpload from "../HeadingCoverUpload/HeadingCoverUpload";
import useUpdateObservationQuestion from "../../hooks/usePutObservationQuestion/usePutObservationQuestion";
import { useCreateObservationQuestion } from "../../hooks";
import usePatchObservationQuestion from "../../hooks/usePatchObservationQuestion/usePatchObservationQuestion";
import CustomToast from "src/components/CustomToast/CustomToast";
import { toast } from "react-toastify";
import { getQuestionIdByType } from "src/utils/getQuestionType";
import { Info } from "lucide-react";

interface Props extends AdminObservationQuestion {
	questionIndex: number;
	isQuestionActionsVisible?: boolean;
	removeQuestion: () => void;
	moveQuestionDown: (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => void;
	moveQuestionUp: (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => void;
}

const AdminObservationHeading: FC<Props> = ({
	questionIndex,
	isQuestionActionsVisible,
	removeQuestion,
	moveQuestionDown,
	moveQuestionUp
}) => {
	const { control, setValue } = useFormContext<AdminObservationForm>();

	const { errors } = useFormState({ control });

	const { field: questionField } = useController({
		control,
		name: `questions.${questionIndex}.question`,
		rules: { required: "Please complete required field" }
	});

	const { field: noteField } = useController({
		control,
		name: `questions.${questionIndex}.note`
	});

	const question = useWatch({
		control,
		name: `questions.${questionIndex}`
	});

	const [uuid, setUUID] = useState<string | null>(null);

	const {
		done,
		error: uploadError,
		uploading,
		upload
	} = useAzureFileUpload((uuidList) => {
		if (!uuidList) return;

		if (Array.isArray(uuidList) && question.id) {
			patchObservationImage(
				{
					id: question.id,
					image_cover_id: uuidList[0]
				},
				{
					onSuccess: () => {
						setUUID(uuidList[0]);

						setValue(`questions.${questionIndex}.image`, uuidList[0]);
					},
					onError: () => {
						toast.error(
							<CustomToast
								type="Error"
								message="Please try uploading the image again"
							/>
						);
					}
				}
			);
		}
	});

	const { observationFormId } = useWatch({ control });

	const { mutate: updateObservationQuestion, isLoading: isUpdatingQuestion } =
		useUpdateObservationQuestion();

	const { mutate: patchObservationImage } = usePatchObservationQuestion();

	const { mutate: createObservationQuestion, isLoading: isSubmittingQuestion } =
		useCreateObservationQuestion();

	const questions = useWatch({ control, name: "questions" });

	const onCreateQuestionSuccess = ({ id }: IAdminQuestion) => {
		setValue(`questions.${questionIndex}.id`, id);
	};

	const onCreateQuestionError = () => {
		toast.error(
			<CustomToast
				type="Error"
				message={"Error duplicating the form please try again"}
			/>
		);
	};

	const handleSubmitQuestion = async () => {
		questionField.onBlur();

		if (question.id) {
			updateObservationQuestion({
				id: question.id,
				observation_form_id: observationFormId,
				question_type_id:
					question.questionTypeId ??
					getQuestionIdByType(
						question.type?.toUpperCase() as IQuestionTypeEnum
					),
				question: question.question,
				note: question.note,
				order: question.order,
				image_cover_id: question.image
			});
			return;
		}

		createObservationQuestion(
			{
				observation_form_id: observationFormId,
				question_type_id:
					question.questionTypeId ??
					getQuestionIdByType(
						question.type?.toUpperCase() as IQuestionTypeEnum
					),
				order: question.order,
				question: question.question,
				note: question.note,
				image_cover_id: question.image
			},
			{
				onSuccess: (data) => onCreateQuestionSuccess(data),
				onError: onCreateQuestionError
			}
		);
	};

	useEffect(() => {
		if (!uploadError) return;

		toast.error(
			<CustomToast
				type="Error"
				message="Please try uploading the image again"
			/>
		);
	}, [uploadError]);

	useEffect(() => {
		setValue(`questions.${questionIndex}.answers`, []);
	}, []);

	const questionError =
		errors.questions && errors.questions[questionIndex]?.question;

	return (
		<div className="flex relative flex-col rounded-xl border border-border bg-subtle shadow-sm p-6">
			{isQuestionActionsVisible && (
				<QuestionActions
					questionIndex={questionIndex}
					questions={questions}
					moveQuestionUp={moveQuestionUp}
					moveQuestionDown={moveQuestionDown}
					removeQuestion={removeQuestion}
					isBranchButtonVisible={false}
				/>
			)}

			<div>
				<h4 className="font-heading text-lg font-medium text-foreground mb-6">
					Heading
				</h4>

				<div className="flex flex-col gap-4">
					<div className={questionError ? "has-error" : ""}>
						<div className="flex justify-between mb-1">
							<label className="text-sm font-medium text-zinc-600 mb-1 block">
								Text
							</label>

							<span className="text-[13px] text-muted-foreground">
								Required
							</span>
						</div>

						<div className="relative">
							<input
								type="text"
								className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
								placeholder="Enter heading text"
								{...questionField}
								onBlur={handleSubmitQuestion}
								disabled={isSubmittingQuestion || isUpdatingQuestion}
								autoFocus={true}
							/>

							{(isSubmittingQuestion || isUpdatingQuestion) && (
								<div className="absolute right-3 top-1/2 -translate-y-1/2">
									<div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
								</div>
							)}
						</div>

						{questionError && (
							<div className="flex items-center gap-1 text-destructive mt-1">
								<Info size={16} />
								<span>
									{errors.questions?.[questionIndex]?.question?.message}
								</span>
							</div>
						)}
					</div>

					<div>
						<label className="text-sm font-medium text-zinc-600 mb-1 block">
							Note
						</label>

						<input
							type="text"
							className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
							placeholder="Add any additional information to support this heading"
							{...noteField}
							onBlur={handleSubmitQuestion}
							disabled={isSubmittingQuestion || isUpdatingQuestion}
						/>
					</div>

					<HeadingCoverUpload
						questionIndex={questionIndex}
						control={control}
						error={uploadError}
						upload={upload}
						uuid={uuid}
						done={done}
						uploading={uploading}
						setValue={setValue}
						setUUID={setUUID}
						questionId={question.id}
					/>
				</div>
			</div>
		</div>
	);
};

export default AdminObservationHeading;
